import React, { useEffect, useRef, useState } from "react";
import { useToast } from "../../common/toast/ToastContext";
import {
  fetchAllWebImages,
  uploadWebImage,
  replaceWebImage,
  deleteWebImage,
} from "./adminWebImageService";
import "./webImage.css";

const BASE_URL = "https://speakboldwithkk.com";
const CATEGORIES = ["POSTER", "PROMOTION", "BANNER", "ANNOUNCEMENT"];
const emptyForm = { name: "", category: "BANNER" };

const AdminWebImagePage = () => {
  const { showToast } = useToast();
  const [images, setImages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [file, setFile] = useState(null);
  const [saving, setSaving] = useState(false);
  const replaceInputRefs = useRef({});

  const load = async () => {
    setLoading(true);
    const res = await fetchAllWebImages();
    if (res.status === 200) setImages(res.data);
    else showToast(res.message || "Failed to load images", "error");
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleUpload = async () => {
    if (!file || !form.name.trim()) {
      showToast("Name and image file are required", "error");
      return;
    }
    setSaving(true);
    const res = await uploadWebImage(file, form.name, form.category);
    if (res.status === 200 || res.status === 201) {
      showToast("Image uploaded successfully", "success");
      setShowForm(false);
      setForm(emptyForm);
      setFile(null);
      load();
    } else {
      showToast(res.message || "Upload failed", "error");
    }
    setSaving(false);
  };

  const handleReplace = async (id, e) => {
    const f = e.target.files[0];
    if (!f) return;
    const res = await replaceWebImage(id, f);
    if (res.status === 200) {
      showToast("Image replaced", "success");
      load();
    } else {
      showToast(res.message || "Replace failed", "error");
    }
    e.target.value = "";
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this image?")) return;
    const res = await deleteWebImage(id);
    if (res.status === 200 || res.status === 204) {
      showToast("Image deleted", "success");
      load();
    } else {
      showToast(res.message || "Delete failed", "error");
    }
  };

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h2>Web Images</h2>
        <button onClick={() => { setShowForm(true); setForm(emptyForm); setFile(null); }}>
          + Upload Image
        </button>
      </div>

      {showForm && (
        <div className="form-card">
          <h3>Upload New Image</h3>
          <div className="wi-form-grid">
            <div className="form-group">
              <label>Name</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Image name"
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <select
                value={form.category}
                onChange={(e) => setForm({ ...form, category: e.target.value })}
              >
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </div>
            <div className="form-group">
              <label>Image File</label>
              <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files[0])} />
            </div>
          </div>
          <div className="form-actions">
            <button className="btn primary" onClick={handleUpload} disabled={saving}>
              {saving ? "Uploading..." : "Upload"}
            </button>
            <button className="btn secondary" onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {loading ? (
        <p>Loading...</p>
      ) : images.length === 0 ? (
        <p className="wi-empty">No images uploaded yet.</p>
      ) : (
        <div className="wi-grid">
          {images.map((img) => (
            <div key={img.id} className="wi-card">
              <img
                src={`${BASE_URL}${img.imageUrl}`}
                alt={img.name}
                className="wi-thumb"
              />
              <div className="wi-info">
                <span className="wi-name">{img.name}</span>
                <span className="wi-badge">{img.category}</span>
              </div>
              <div className="wi-actions">
                <input
                  type="file"
                  accept="image/*"
                  style={{ display: "none" }}
                  ref={(el) => (replaceInputRefs.current[img.id] = el)}
                  onChange={(e) => handleReplace(img.id, e)}
                />
                <button
                  className="btn secondary"
                  onClick={() => replaceInputRefs.current[img.id]?.click()}
                >
                  Replace
                </button>
                <button className="btn delete-btn" onClick={() => handleDelete(img.id)}>
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdminWebImagePage;
